class BAOProductForm extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.form = this.closest('form')

    this.variantInput = this.querySelector(
      '[data-product-variant-id]',
    )

    this.sizeInputs = this.querySelectorAll(
      '[data-product-size]',
    )

    this.atcButton = this.querySelector(
      '[data-product-atc]',
    )

    this.atcText = this.querySelector(
      '[data-product-atc-text]',
    )

    this.selectedSize = this.querySelector(
      '[data-product-selected-size]',
    )

    this.inventoryEl = this.querySelector(
      '[data-product-inventory]',
    )

    this.errorWrapper = this.querySelector(
      '[data-product-form-el="error-wrapper"]',
    )

    this.errorMessage = this.querySelector(
      '[data-product-form-el="error-message"]',
    )

    /*
     * Bundle Slip
     */
    this.slipContainer = this.querySelector(
      '[data-bundle-slip]',
    )

    this.slipTitle = this.querySelector(
      '[data-bundle-slip-title]',
    )

    this.slipColourInput = this.querySelector(
      '[data-bundle-slip-colour]',
    )

    this.slipVariantInput = this.querySelector(
      '[data-bundle-slip-variant-id]',
    )

    this.slipIdInput = this.querySelector(
      '[data-bundle-slip-id]',
    )

    this.slipProductIdInput = this.querySelector(
      '[data-bundle-slip-product-id]',
    )

    this.slipParentIdInput = this.querySelector(
      '[data-bundle-slip-parent-id]',
    )

    this.slipSwatchGroups = this.querySelectorAll(
      '[data-bundle-slip-swatches]',
    )

    this.slipSwatches = this.querySelectorAll(
      '[data-bundle-slip-swatch]',
    )

    if (
      !this.form ||
      !this.variantInput ||
      !this.atcButton ||
      !this.atcText
    ) {
      return
    }

    this.setupSizeListeners()
    this.setupSlipListeners()

    this.listeners.add(
      this.form,
      'submit',
      this.onSubmit.bind(this),
    )

    this.listeners.add(
      window,
      'pageshow',
      this.onPageShow.bind(this),
    )

    this.initialise()
  }

  /*
  |--------------------------------------------------------------------------
  | Initialise
  |--------------------------------------------------------------------------
  */

  initialise () {
    if (this.sizeInputs.length) {
      this.resetSizeSelection()

      return
    }

    this.initialiseNoSizeProduct()
  }

  initialiseNoSizeProduct () {
    this.updateInventoryMessage(this.atcButton)
    this.syncStickyAtc()

    const variantId = this.variantInput.value

    if (!variantId) {
      return
    }

    if (this.slipContainer) {
      this.slipContainer.hidden = false

      this.updateSlipForVariant(variantId)
    }

    this.updateButtonState()
  }

  /*
  |--------------------------------------------------------------------------
  | Listeners
  |--------------------------------------------------------------------------
  */

  setupSizeListeners () {
    this.sizeInputs.forEach((input) => {
      this.listeners.add(
        input,
        'change',
        this.onSizeChange.bind(this),
      )
    })
  }

  setupSlipListeners () {
    this.slipSwatches.forEach((swatch) => {
      this.listeners.add(
        swatch,
        'click',
        this.onSlipSwatchClick.bind(this),
      )
    })
  }

  /*
  |--------------------------------------------------------------------------
  | Size
  |--------------------------------------------------------------------------
  */

  resetSizeSelection () {
    this.sizeInputs.forEach((input) => {
      input.checked = false
    })

    this.variantInput.value = ''

    this.atcButton.disabled = true

    this.atcButton.removeAttribute(
      'data-preorder',
    )

    this.atcButton.removeAttribute(
      'aria-disabled',
    )

    this.atcButton.removeAttribute(
      'aria-busy',
    )

    this.atcText.textContent =
      window.product.select_size

    if (this.selectedSize) {
      this.selectedSize.textContent = ''
    }

    this.updateInventoryMessage(null)
    this.syncStickyAtc()

    /*
     * Hide the complete slip section until
     * a main product size has been selected.
     */
    if (this.slipContainer) {
      this.slipContainer.hidden = true
    }

    /*
     * Reset all slip groups.
     */
    this.slipSwatchGroups.forEach((group) => {
      group.hidden = true
    })

    /*
     * Clear any existing slip selection.
     */
    this.clearSlipSelection()

    this.handleErrorMessage()
  }

  onSizeChange (event) {
    const input = event.currentTarget
    const variantId = input.dataset.variantId

    if (!variantId) {
      return
    }

    this.variantInput.value = variantId

    if (this.selectedSize) {
      this.selectedSize.textContent =
        `: ${input.value}`
    }

    this.handleErrorMessage()

    if (this.slipContainer) {
      /*
       * Reveal the slip section now that
       * a size has been selected.
       */
      this.slipContainer.hidden = false

      this.updateSlipForVariant(
        variantId,
      )
    }

    this.updateButtonState()
    this.updateInventoryMessage(input)
    this.updateSizeGuideFit(input)
    this.syncStickyAtc()
  }

  updateSizeGuideFit (input) {
    document.dispatchEvent(
      new CustomEvent('size-guide:fit-update', {
        detail: {
          sectionId: this.dataset.sectionId || '',
          fittingInfo: input.dataset.fittingInfo || '',
        },
      }),
    )
  }

  /*
  |--------------------------------------------------------------------------
  | Slip
  |--------------------------------------------------------------------------
  */

  updateSlipForVariant (variantId) {
    if (!this.slipContainer) {
      return true
    }

    const currentVariantId =
      String(variantId)

    /*
     * Only show the slip swatches belonging
     * to the selected main product variant.
     */
    this.slipSwatchGroups.forEach((group) => {
      const isCurrentGroup =
        group.dataset.parentVariantId ===
        currentVariantId

      group.hidden = !isCurrentGroup
    })

    /*
     * Find the slip variants belonging
     * to this main product variant.
     */
    const currentSwatches = Array.from(
      this.slipSwatches,
    ).filter(
      (swatch) =>
        swatch.dataset.parentVariantId ===
        currentVariantId,
    )

    /*
     * Remove any previous selection.
     */
    this.slipSwatches.forEach((swatch) => {
      swatch.setAttribute(
        'aria-pressed',
        'false',
      )
    })

    if (!currentSwatches.length) {
      this.clearSlipSelection()

      return false
    }

    /*
     * Automatically select the first
     * available slip.
     */
    const firstAvailableSlip =
      currentSwatches.find(
        (swatch) =>
          swatch.dataset.available === 'true',
      )

    if (!firstAvailableSlip) {
      this.clearSlipSelection()

      return false
    }

    this.selectSlip(
      firstAvailableSlip,
    )

    return true
  }

  selectSlip (swatch) {
    if (!swatch) {
      return
    }

    if (
      swatch.dataset.available !== 'true'
    ) {
      return
    }

    const variantId =
      swatch.dataset.variantId

    const productId =
      swatch.dataset.productId

    const colour =
      swatch.dataset.colour

    /*
     * Remove selected state from all slips.
     */
    this.slipSwatches.forEach((item) => {
      item.setAttribute(
        'aria-pressed',
        'false',
      )
    })

    /*
     * Select the requested slip.
     */
    swatch.setAttribute(
      'aria-pressed',
      'true',
    )

    /*
     * Update visible colour.
     */
    if (this.slipTitle) {
      this.slipTitle.textContent =
        colour || ''
    }

    /*
     * Update main product properties.
     */
    if (this.slipColourInput) {
      this.slipColourInput.value =
        colour || ''
    }

    if (this.slipVariantInput) {
      this.slipVariantInput.value =
        variantId || ''
    }

    /*
     * Update bundle slip item.
     */
    if (this.slipIdInput) {
      this.slipIdInput.disabled = false

      this.slipIdInput.value =
        variantId || ''
    }

    if (this.slipProductIdInput) {
      this.slipProductIdInput.disabled = false

      this.slipProductIdInput.value =
        productId || ''
    }

    if (this.slipParentIdInput) {
      this.slipParentIdInput.disabled = false

      this.slipParentIdInput.value =
        this.variantInput.value || ''
    }
  }

  clearSlipSelection () {
    this.slipSwatches.forEach((swatch) => {
      swatch.setAttribute(
        'aria-pressed',
        'false',
      )
    })

    if (this.slipTitle) {
      this.slipTitle.textContent = ''
    }

    if (this.slipColourInput) {
      this.slipColourInput.value = ''
    }

    if (this.slipVariantInput) {
      this.slipVariantInput.value = ''
    }

    if (this.slipIdInput) {
      this.slipIdInput.value = ''
      this.slipIdInput.disabled = true
    }

    if (this.slipProductIdInput) {
      this.slipProductIdInput.value = ''
      this.slipProductIdInput.disabled = true
    }

    if (this.slipParentIdInput) {
      this.slipParentIdInput.value =
        this.variantInput.value || ''
    }
  }

  onSlipSwatchClick (event) {
    const swatch = event.currentTarget

    if (
      swatch.dataset.available !== 'true'
    ) {
      return
    }

    /*
     * Ignore a slip belonging to another
     * main product variant.
     */
    if (
      swatch.dataset.parentVariantId !==
      String(this.variantInput.value)
    ) {
      return
    }

    this.selectSlip(swatch)

    this.updateButtonState()
  }

  hasAvailableSelectedSlip () {
    if (!this.slipContainer) {
      return true
    }

    const currentVariantId =
      String(this.variantInput.value)

    const currentSwatches = Array.from(
      this.slipSwatches,
    ).filter(
      (swatch) =>
        swatch.dataset.parentVariantId ===
        currentVariantId,
    )

    /*
     * A variant without a slip isn't treated
     * as requiring a slip.
     */
    if (!currentSwatches.length) {
      return true
    }

    const selectedSlip =
      currentSwatches.find(
        (swatch) =>
          swatch.getAttribute(
            'aria-pressed',
          ) === 'true' &&
          swatch.dataset.available ===
            'true',
      )

    return Boolean(selectedSlip)
  }

  /*
  |--------------------------------------------------------------------------
  | Submit
  |--------------------------------------------------------------------------
  */

  async onSubmit (event) {
    event.preventDefault()

    if (
      !this.variantInput.value ||
      this.atcButton.disabled
    ) {
      return
    }

    if (
      this.slipContainer &&
      !this.hasAvailableSelectedSlip()
    ) {
      return
    }

    this.handleErrorMessage()

    this.addBundleProperties()

    this.setLoadingState(true)

    try {
      await this.addToCart()
    } catch (error) {
      this.handleErrorMessage(
        error?.description ||
        error?.message ||
        window.variantStrings.unavailable,
      )
    } finally {
      this.setLoadingState(false)
    }
  }

  async addToCart () {
    return window.BAO.Cart.addItemFromForm(
      this.form,
    )
  }

  /*
  |--------------------------------------------------------------------------
  | Bundle
  |--------------------------------------------------------------------------
  */

  addBundleProperties () {
    if (!this.slipContainer) {
      return
    }

    if (
      !this.slipIdInput ||
      this.slipIdInput.disabled ||
      !this.slipIdInput.value
    ) {
      return
    }

    const mainVariantId =
      this.variantInput.value

    if (!mainVariantId) {
      return
    }

    this.slipIdInput.disabled = false

    if (this.slipProductIdInput) {
      this.slipProductIdInput.disabled = false
    }

    if (this.slipParentIdInput) {
      this.slipParentIdInput.disabled = false

      this.slipParentIdInput.value =
        mainVariantId
    }

    /*
     * Remove previously generated bundle
     * properties before creating a new ID.
     */
    const existingMainBundleInput =
      this.form.querySelector(
        '[name="properties[__venn_bundle_id]"]',
      )

    const existingSlipBundleInput =
      this.form.querySelector(
        '[name="items[1][properties][__venn_bundle_id]"]',
      )

    existingMainBundleInput?.remove()
    existingSlipBundleInput?.remove()

    /*
     * Shared bundle ID.
     */
    const bundleId =
      `${mainVariantId}_${Date.now()}`

    /*
     * Main product bundle property.
     */
    const mainBundleInput =
      document.createElement('input')

    mainBundleInput.type = 'hidden'

    mainBundleInput.name =
      'properties[__venn_bundle_id]'

    mainBundleInput.value =
      bundleId

    this.form.appendChild(
      mainBundleInput,
    )

    /*
     * Slip bundle property.
     */
    const slipBundleInput =
      document.createElement('input')

    slipBundleInput.type = 'hidden'

    slipBundleInput.name =
      'items[1][properties][__venn_bundle_id]'

    slipBundleInput.value =
      bundleId

    this.form.appendChild(
      slipBundleInput,
    )
  }

  /*
  |--------------------------------------------------------------------------
  | Button
  |--------------------------------------------------------------------------
  */

  updateInventoryMessage (source) {
    if (!this.inventoryEl) {
      return
    }

    if (this.dataset.showInventoryMessage !== 'true') {
      this.inventoryEl.hidden = true
      this.inventoryEl.textContent = ''
      return
    }

    const threshold = Number(this.dataset.inventoryThreshold || 10)
    const showQuantity = this.dataset.showInventoryQuantity === 'true'

    let quantity = null
    let management = null

    if (source) {
      quantity = Number(source.dataset.inventoryQuantity)
      management = source.dataset.inventoryManagement
    } else if (!this.sizeInputs.length) {
      quantity = Number(this.atcButton?.dataset.inventoryQuantity)
      management = this.atcButton?.dataset.inventoryManagement
    }

    if (
      management !== 'shopify' ||
      Number.isNaN(quantity) ||
      quantity <= 0 ||
      threshold <= 0 ||
      quantity > threshold
    ) {
      this.inventoryEl.hidden = true
      this.inventoryEl.textContent = ''
      return
    }

    if (showQuantity) {
      const template =
        this.dataset.inventoryLowCount ||
        'Limited Availability! COUNT Left in stock'
      this.inventoryEl.textContent = template.replace(
        'COUNT',
        String(quantity),
      )
    } else {
      this.inventoryEl.textContent =
        this.dataset.inventoryLow || 'Limited Availability!'
    }

    this.inventoryEl.hidden = false
  }

  updateButtonState () {
    /*
     * Products without a Size option.
     */
    if (!this.sizeInputs.length) {
      const available =
        this.atcButton.dataset.variantAvailable ===
        'true'

      const isPreOrder =
        this.atcButton.dataset.variantPreOrder ===
        'true'

      const quantitySoldOut =
        this.atcButton.dataset.variantQuantitySoldout ===
        'true'

      if (
        (!available && !isPreOrder) ||
        quantitySoldOut
      ) {
        this.atcButton.removeAttribute(
          'data-preorder',
        )

        this.toggleAddButton(
          true,
          window.variantStrings.soldOut,
        )

        return
      }

      if (
        this.slipContainer &&
        !this.hasAvailableSelectedSlip()
      ) {
        this.atcButton.removeAttribute(
          'data-preorder',
        )

        this.toggleAddButton(
          true,
          window.variantStrings.soldOut,
        )

        return
      }

      if (isPreOrder) {
        this.atcButton.dataset.preorder =
          'true'

        this.toggleAddButton(
          false,
          window.variantStrings.preorder,
        )

        return
      }

      this.atcButton.removeAttribute(
        'data-preorder',
      )

      this.toggleAddButton(
        false,
        window.variantStrings.addToCart,
      )

      return
    }

    /*
     * Products with a Size option.
     */
    const selectedInput = Array.from(
      this.sizeInputs,
    ).find(
      (input) => input.checked,
    )

    if (!selectedInput) {
      this.atcButton.removeAttribute(
        'data-preorder',
      )

      this.toggleAddButton(
        true,
        window.product.select_size,
      )

      return
    }

    const available =
      selectedInput.dataset.variantAvailable ===
      'true'

    const isPreOrder =
      selectedInput.dataset.variantPreOrder ===
      'true'

    const quantitySoldOut =
      selectedInput.dataset.variantQuantitySoldout ===
      'true'

    /*
     * Main variant sold out.
     */
    if (
      (!available && !isPreOrder) ||
      quantitySoldOut
    ) {
      this.atcButton.removeAttribute(
        'data-preorder',
      )

      this.toggleAddButton(
        true,
        window.variantStrings.soldOut,
      )

      return
    }

    /*
     * Required slip exists but has no
     * available selected variant.
     */
    if (
      this.slipContainer &&
      !this.hasAvailableSelectedSlip()
    ) {
      this.atcButton.removeAttribute(
        'data-preorder',
      )

      this.toggleAddButton(
        true,
        window.variantStrings.soldOut,
      )

      return
    }

    /*
     * Pre-order.
     */
    if (isPreOrder) {
      this.atcButton.dataset.preorder =
        'true'

      this.toggleAddButton(
        false,
        window.variantStrings.preorder,
      )

      return
    }

    /*
     * Normal Add to Cart.
     */
    this.atcButton.removeAttribute(
      'data-preorder',
    )

    this.toggleAddButton(
      false,
      window.variantStrings.addToCart,
    )
  }

  toggleAddButton (disable = true, text) {
    if (
      !this.atcButton ||
      !this.atcText
    ) {
      return
    }

    if (disable) {
      this.atcButton.setAttribute(
        'disabled',
        'disabled',
      )
    } else {
      this.atcButton.removeAttribute(
        'disabled',
      )
    }

    if (text) {
      this.atcText.textContent = text
    }

    this.syncStickyAtc()
  }

  syncStickyAtc () {
    const sticky = document.querySelector('[data-sticky-atc]')
    const stickyButton = document.querySelector('[data-sticky-atc-button]')
    const stickyText = document.querySelector('[data-sticky-atc-text]')

    if (
      !sticky ||
      !stickyButton ||
      !stickyText ||
      !this.atcButton ||
      !this.atcText
    ) {
      return
    }

    stickyText.textContent = this.atcText.textContent

    stickyButton.disabled = false
    stickyButton.setAttribute(
      'aria-disabled',
      String(this.atcButton.disabled),
    )

    if (this.atcButton.hasAttribute('data-preorder')) {
      stickyButton.setAttribute('data-preorder', 'true')
    } else {
      stickyButton.removeAttribute('data-preorder')
    }
  }

  setLoadingState (loading) {
    if (!this.atcButton) {
      return
    }

    if (loading) {
      this.atcButton.disabled = true

      this.atcButton.setAttribute(
        'aria-disabled',
        'true',
      )

      this.atcButton.setAttribute(
        'aria-busy',
        'true',
      )

      return
    }

    this.atcButton.removeAttribute(
      'aria-disabled',
    )

    this.atcButton.removeAttribute(
      'aria-busy',
    )

    this.updateButtonState()
  }

  setUnavailable () {
    if (
      !this.atcButton ||
      !this.atcText
    ) {
      return
    }

    this.atcButton.removeAttribute(
      'data-preorder',
    )

    this.atcButton.disabled = true

    this.atcText.textContent =
      window.variantStrings.unavailable
  }

  /*
  |--------------------------------------------------------------------------
  | Errors
  |--------------------------------------------------------------------------
  */

  handleErrorMessage (errorMessage = false) {
    if (
      !this.errorWrapper ||
      !this.errorMessage
    ) {
      return
    }

    this.errorWrapper.toggleAttribute(
      'hidden',
      !errorMessage,
    )

    if (errorMessage) {
      this.errorMessage.textContent =
        errorMessage
    } else {
      this.errorMessage.textContent = ''
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Page Show
  |--------------------------------------------------------------------------
  */

  onPageShow () {
    if (this.sizeInputs.length) {
      this.resetSizeSelection()

      return
    }

    this.initialiseNoSizeProduct()
  }
}

if (!customElements.get('bao-product-form')) {
  customElements.define(
    'bao-product-form',
    BAOProductForm,
  )
}
